import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { departmentsAPI, projectsAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Common/Modal';
import {
  PlusIcon,
  FolderIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  BuildingOffice2Icon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const getDeptColor = (dept) => {
  const n = (dept.name || '').toLowerCase();
  if (n.includes('hr') || n.includes('human resource')) return '#16a34a';
  if (n.includes('finance')) return '#d97706';
  if (n.includes('account')) return '#2563eb';
  if (n.includes('it') || n.includes('tech')) return '#7c3aed';
  return dept.color || '#4f46e5';
};

export default function Dashboard() {
  const { user, isAdmin, isSuperAdmin, canCreate } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewDept, setShowNewDept] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', department_id: '' });
  const [newDept, setNewDept] = useState({ name: '', description: '', color: '#6366f1' });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await departmentsAPI.list();
      setDepartments(res.data);
      for (const dept of res.data) {
        projectsAPI
          .list(dept.id)
          .then((r) => setProjects((prev) => ({ ...prev, [dept.id]: r.data })));
      }
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      const res = await projectsAPI.create(newProject);
      setProjects((prev) => ({
        ...prev,
        [newProject.department_id]: [...(prev[newProject.department_id] || []), res.data],
      }));
      setShowNewProject(false);
      setNewProject({ name: '', description: '', department_id: '' });
      toast.success('Project created');
      navigate(`/project/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    }
  };

  const createDept = async (e) => {
    e.preventDefault();
    try {
      const res = await departmentsAPI.create(newDept);
      setDepartments((prev) => [...prev, res.data]);
      setShowNewDept(false);
      setNewDept({ name: '', description: '', color: '#6366f1' });
      toast.success('Department created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create department');
    }
  };

  const allProjects = Object.values(projects).flat();
  const totalDocs = allProjects.reduce((sum, p) => sum + (p.document_count || 0), 0);
  const totalProjects = allProjects.length;
  // The "New Project" header button is only useful if the user can create
  // somewhere — otherwise hide it.
  const canCreateAnywhere = isSuperAdmin || departments.some((d) => canCreate(d.id));

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Welcome back,{' '}
            <span className="font-medium text-slate-700">{user?.full_name}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowNewDept(true)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <PlusIcon className="w-4 h-4" /> New Department
            </button>
          )}
          {canCreateAnywhere && (
            <button
              onClick={() => setShowNewProject(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm"
            >
              <PlusIcon className="w-4 h-4" /> New Project
            </button>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={FolderOpenIcon}
          label="Active Projects"
          value={totalProjects}
          iconColor="#4f46e5"
          iconBg="#eef2ff"
        />
        <StatCard
          icon={DocumentTextIcon}
          label="Total Documents"
          value={totalDocs}
          iconColor="#0891b2"
          iconBg="#ecfeff"
        />
        <StatCard
          icon={BuildingOffice2Icon}
          label="Departments"
          value={departments.length}
          iconColor="#d97706"
          iconBg="#fffbeb"
        />
        <StatCard
          icon={RectangleGroupIcon}
          label="Total Projects"
          value={departments.reduce((s, d) => s + (d.project_count || 0), 0)}
          iconColor="#16a34a"
          iconBg="#f0fdf4"
        />
      </div>

      {departments.length === 0 ? (
        <EmptyState isAdmin={isAdmin} onNew={() => setShowNewDept(true)} />
      ) : (
        <div className="flex gap-6 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-8">
            {departments.map((dept) => {
              const color = getDeptColor(dept);
              const deptProjects = projects[dept.id] || [];

              return (
                <div key={dept.id}>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: color + '18', color }}
                    >
                      {dept.name.charAt(0)}
                    </div>
                    <div className="flex items-baseline gap-2 min-w-0">
                      <h2 className="font-bold text-slate-800">{dept.name}</h2>
                      {dept.description && (
                        <span className="text-sm text-slate-400 truncate hidden sm:inline">
                          — {dept.description}
                        </span>
                      )}
                    </div>
                    <span
                      className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color + '15', color }}
                    >
                      {deptProjects.length} project{deptProjects.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {deptProjects.map((proj) => (
                      <ProjectCard key={proj.id} project={proj} deptColor={color} />
                    ))}
                    {canCreate(dept.id) && (
                      <NewProjectButton
                        onClick={() => {
                          setNewProject({
                            name: '',
                            description: '',
                            department_id: String(dept.id),
                          });
                          setShowNewProject(true);
                        }}
                        color={color}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right panel */}
          <div className="w-72 flex-shrink-0 space-y-4">
            {/* Department overview */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-50">
                <h3 className="text-sm font-semibold text-slate-800">Department Overview</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {departments.map((dept) => {
                  const color = getDeptColor(dept);
                  const deptProjects = projects[dept.id] || [];
                  const docCount = deptProjects.reduce(
                    (s, p) => s + (p.document_count || 0),
                    0
                  );
                  return (
                    <div key={dept.id} className="px-5 py-3.5 flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: color + '18', color }}
                      >
                        {dept.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{dept.name}</p>
                        <p className="text-xs text-slate-400">
                          {deptProjects.length} project{deptProjects.length !== 1 ? 's' : ''} ·{' '}
                          {docCount} doc{docCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent projects */}
            {allProjects.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50">
                  <h3 className="text-sm font-semibold text-slate-800">Recent Projects</h3>
                </div>
                <div className="divide-y divide-slate-50">
                  {[...allProjects]
                    .reverse()
                    .slice(0, 5)
                    .map((proj) => {
                      const dept = departments.find((d) => d.id === proj.department_id);
                      const color = dept ? getDeptColor(dept) : '#4f46e5';
                      return (
                        <Link
                          key={proj.id}
                          to={`/project/${proj.id}`}
                          className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                        >
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: color + '18' }}
                          >
                            <FolderIcon className="w-3.5 h-3.5" style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-700 truncate">
                              {proj.name}
                            </p>
                            <p className="text-[11px] text-slate-400">
                              {proj.document_count} doc{proj.document_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Project Modal */}
      <Modal
        open={showNewProject}
        onClose={() => setShowNewProject(false)}
        title="Create New Project"
      >
        <form onSubmit={createProject} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Project Name *
            </label>
            <input
              required
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              placeholder="e.g. Q4 Recruitment"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              rows={2}
              placeholder="Optional description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Department *
            </label>
            <select
              required
              value={newProject.department_id}
              onChange={(e) =>
                setNewProject({ ...newProject, department_id: e.target.value })
              }
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
            >
              <option value="">Select department...</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowNewProject(false)}
              className="px-4 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 shadow-sm transition-colors"
            >
              Create Project
            </button>
          </div>
        </form>
      </Modal>

      {/* New Department Modal */}
      <Modal
        open={showNewDept}
        onClose={() => setShowNewDept(false)}
        title="Create New Department"
      >
        <form onSubmit={createDept} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              required
              value={newDept.name}
              onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              placeholder="e.g. Marketing"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input
              value={newDept.description}
              onChange={(e) => setNewDept({ ...newDept, description: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
            <input
              type="color"
              value={newDept.color}
              onChange={(e) => setNewDept({ ...newDept, color: e.target.value })}
              className="h-10 w-20 rounded-lg border border-slate-200 cursor-pointer"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowNewDept(false)}
              className="px-4 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 shadow-sm transition-colors"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, iconColor, iconBg }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5 flex items-center gap-4">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: iconBg }}
      >
        <Icon className="w-5 h-5" style={{ color: iconColor }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

function ProjectCard({ project, deptColor }) {
  return (
    <Link
      to={`/project/${project.id}`}
      className="group bg-white rounded-2xl border border-slate-100 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all p-5 flex flex-col justify-between min-h-[120px]"
    >
      <div>
        <div
          className="w-9 h-9 rounded-xl mb-3 flex items-center justify-center"
          style={{ backgroundColor: deptColor + '18' }}
        >
          <FolderIcon className="w-5 h-5" style={{ color: deptColor }} />
        </div>
        <h3 className="font-semibold text-slate-800 text-sm group-hover:text-primary-600 transition-colors truncate">
          {project.name}
        </h3>
        {project.description && (
          <p className="text-xs text-slate-400 truncate mt-0.5">{project.description}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-3">
        <DocumentTextIcon className="w-3.5 h-3.5 flex-shrink-0" />
        {project.document_count} document{project.document_count !== 1 ? 's' : ''}
      </div>
    </Link>
  );
}

function NewProjectButton({ onClick, color }) {
  return (
    <button
      onClick={onClick}
      className="min-h-[120px] rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-300 text-slate-400 hover:text-slate-500 flex flex-col items-center justify-center gap-2 transition-all hover:bg-slate-50/50"
    >
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: color + '12' }}
      >
        <PlusIcon className="w-4 h-4" style={{ color: color + 'cc' }} />
      </div>
      <span className="text-xs font-medium">New Project</span>
    </button>
  );
}

function EmptyState({ isAdmin, onNew }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mb-5">
        <BuildingOffice2Icon className="w-10 h-10 text-slate-400" />
      </div>
      <h3 className="text-base font-semibold text-slate-700 mb-2">No departments yet</h3>
      <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
        {isAdmin
          ? 'Create your first department to start organizing projects and teams.'
          : 'Your administrator needs to create departments first.'}
      </p>
      {isAdmin && (
        <button
          onClick={onNew}
          className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm"
        >
          <PlusIcon className="w-4 h-4" /> Create Department
        </button>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="max-w-7xl mx-auto animate-pulse">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="h-6 w-32 bg-slate-200 rounded-lg mb-2" />
          <div className="h-4 w-48 bg-slate-100 rounded-lg" />
        </div>
        <div className="h-9 w-32 bg-slate-200 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 h-20" />
        ))}
      </div>
      <div className="flex gap-6">
        <div className="flex-1 space-y-8">
          {[...Array(2)].map((_, i) => (
            <div key={i}>
              <div className="h-5 w-32 bg-slate-200 rounded-lg mb-4" />
              <div className="grid grid-cols-3 gap-4">
                {[...Array(3)].map((_, j) => (
                  <div
                    key={j}
                    className="h-[120px] bg-white rounded-2xl border border-slate-100"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="w-72 flex-shrink-0">
          <div className="bg-white rounded-2xl border border-slate-100 h-56" />
        </div>
      </div>
    </div>
  );
}
